import { camelize } from 'humps';
import type {
  ConsentSelectionControl,
  ContextFrame,
  ContextFrameNamesModal,
  ContextFrameContext,
  UpdateConsentSelection,
  ContextFrameWithoutPromptField,
  PurposeLimitations,
  PurposeLimitationNames,
  PurposeLimitation,
} from '@nike/privacy-types';
import { NOT_REQUIRED_MODAL_CONTEXT_LEVEL } from '@nike/privacy-shared-logic';

import { PRIVACY_COMPONENT_TYPE } from '../constants';
import type { AcceptOrDeclineAll, Footer, Representation } from '../types';

/**
 * @name getComponentType
 * @description Gets a component type
 * @param {ContextFrame} context
 * @returns {PRIVACY_COMPONENT_TYPE}
 */
const getComponentType = (context: ContextFrame): PRIVACY_COMPONENT_TYPE => {
  if (
    context.metadata.acceptAllLabel ||
    context.metadata.declineAllLabel ||
    context.metadata.contextFrameConfirmLabel
  )
    return PRIVACY_COMPONENT_TYPE.MODAL;

  return PRIVACY_COMPONENT_TYPE.INLINE;
};

/**
 * @name getContextFrameConfirmLabelExist
 * @description Gets a boolean indicating whether a confirm button exists
 * @param {ContextFrame} context
 * @returns {boolean}
 */
const getContextFrameConfirmLabelExist = (context: ContextFrame): boolean =>
  !!context.metadata.contextFrameConfirmLabel;

/**
 * @name checkIsPermissionPromptRequired
 * @description Retrieve should permissions prompt. When the level field is exceeds 2 and the consent field is 'default accept' or 'default decline' in at least one context
 * @function
 * @param {ContextFrameWithoutPromptField} contextFrame - Is a data structure that helps to wrap and present a collection of Contexts.
 * @param {PurposeLimitations} purposeLimitations - Is a collection of licenses that a user consents to for a given purpose.
 * @returns {boolean} Value for the `isPermissionPromptRequired` in a context frame.
 */
const checkIsPermissionPromptRequired = (
  contextFrame: ContextFrameWithoutPromptField,
  purposeLimitations: PurposeLimitations,
): boolean =>
  contextFrame.contexts.some(context => {
    const relatedPurposeLimitation = purposeLimitations.find(
      ({ category }) => category === context.purposeLimitationCategory,
    );

    return !!relatedPurposeLimitation?.isDefault;
  });

/**
 * @name getLimitationsFromContext
 * @description Retrieves a list of PurposeLimitations and their associated Licenses and Consents.
 * @param {ContextFrame} context - A data structure that helps to wrap and present a collection of Contexts.
 * @returns {PurposeLimitations}
 */
const getLimitationsFromContext = (context: ContextFrame): PurposeLimitations =>
  context.contexts.reduce((accumulator, contextItem) => {
    const purposeLimitation = window.NikePrivacy.permissions.get(
      camelize(contextItem.purposeLimitationCategory) as PurposeLimitationNames,
    ) as PurposeLimitation;

    if (!purposeLimitation) return accumulator;

    return [...accumulator, purposeLimitation];
  }, [] as PurposeLimitations);

/**
 * @name getIsPromptRequired
 * @description Gets is prompt required
 * @param {ContextFrame} context
 * @param {PRIVACY_COMPONENT_TYPE} componentType
 * @returns {boolean}
 */
const getIsPromptRequired = (
  context: ContextFrame,
  componentType: PRIVACY_COMPONENT_TYPE,
): boolean => {
  if (componentType === PRIVACY_COMPONENT_TYPE.INLINE) return true;

  const purposeLimitations = getLimitationsFromContext(context);

  return (
    checkIsPermissionPromptRequired(context, purposeLimitations) ||
    !!context.isGuestAndMemberSelectionMismatched
  );
};

/**
 * @name getConsentSelectionToUpdate
 * @description Gets consent selection to update
 * @param {Object} param - Input parameters object
 * @param {ContextFrameContext} param.context - A single context to get selection to update from
 * @param {boolean} param.consent - Consent value of context
 * @returns {ConsentSelectionControl}
 */
const getConsentSelectionToUpdate = ({
  context,
  consent,
}: {
  context: ContextFrameContext;
  consent: boolean;
}): UpdateConsentSelection => ({
  purposeLimitationCategory: context.purposeLimitationCategory,
  consent,
  level: context.level,
  consentSelectionControlType: context.consentSelectionControlMapping.multiPermission[
    context.level
  ] as ConsentSelectionControl,
});

/**
 * @name onAcceptDeclineAllClick
 * @description Accept All / Decline All handler depending on isAcceptAll param
 * @param {Object} param - Input parameters object
 * @param {ContextFrame} param.context - A collection of Contexts
 * @param {boolean} param.isAcceptAll - Shows if it should be Accept All or Decline All handler
 * @param {ContextFrameNamesModal} param.contextFrameName
 * @returns {Promise<void>}
 */
const onAcceptDeclineAllClick = ({
  context,
  isAcceptAll,
  contextFrameName,
}: {
  context: ContextFrame;
  isAcceptAll: boolean;
  contextFrameName: ContextFrameNamesModal;
}): Promise<void> => {
  const contextFrameToUpdate = {
    contextFrameId: context.metadata.contextFrameId,
    contextFrameVersionId: context.metadata.contextFrameVersionId,
    contextFrameConfirmLabelShown: getContextFrameConfirmLabelExist(context),
    consentSelections: context.contexts.reduce((prevValues, context) => {
      const value = getConsentSelectionToUpdate({
        context,
        consent: isAcceptAll,
      });

      return [...prevValues, value];
    }, [] as UpdateConsentSelection[]),
  };

  return window.NikePrivacy.interactions.set(contextFrameToUpdate, contextFrameName);
};

/**
 * @name getAcceptAllButton
 * @description Gets an accept all button
 * @param {ContextFrame} context
 * @param {ContextFrameNamesModal} contextFrameName
 * @returns {AcceptOrDeclineAll|undefined}
 */
const getAcceptAllButton = (
  context: ContextFrame,
  contextFrameName: ContextFrameNamesModal,
): AcceptOrDeclineAll | undefined => {
  if (context.metadata.acceptAllLabel) {
    return {
      title: context.metadata.acceptAllLabel,
      onClick: () => onAcceptDeclineAllClick({ context, isAcceptAll: true, contextFrameName }),
    };
  }

  return;
};

/**
 * @name getDeclineAllButton
 * @description Gets an decline all button
 * @param {ContextFrame} context
 * @param {ContextFrameNamesModal} contextFrameName
 * @returns {AcceptOrDeclineAll|undefined}
 */
const getDeclineAllButton = (
  context: ContextFrame,
  contextFrameName: ContextFrameNamesModal,
): AcceptOrDeclineAll | undefined => {
  if (context.metadata.declineAllLabel) {
    return {
      title: context.metadata.declineAllLabel,
      onClick: () => onAcceptDeclineAllClick({ context, isAcceptAll: false, contextFrameName }),
    };
  }

  return;
};

/**
 * @name getFooter
 * @description Creates a schema for the footer
 * @param {ContextFrame} context
 * @param {ContextFrameNamesModal} contextFrameName
 * @returns {Footer}
 */
const getFooter = (context: ContextFrame, contextFrameName: ContextFrameNamesModal): Footer => {
  const acceptAllButton = getAcceptAllButton(context, contextFrameName);
  const declineAllButton = getDeclineAllButton(context, contextFrameName);

  if (!acceptAllButton && !declineAllButton) return {};

  return {
    acceptAllButton,
    declineAllButton,
  };
};

/**
 * @name filterContextsByComponentType
 * @description To filter contexts by component type.
 * @param {ContextFrame} contextFrame - Retrieved Context frame.
 * @returns {ContextFrame}
 */
const filterContextsByComponentType = (contextFrame: ContextFrame): ContextFrame => ({
  ...contextFrame,
  contexts: contextFrame.contexts.filter(
    context => context.level > NOT_REQUIRED_MODAL_CONTEXT_LEVEL,
  ),
});

/**
 * @name getRepresentation
 * @description Creates a UI schema
 * @param {ContextFrame|undefined} context
 * @param {ContextFrameNamesModal} contextFrameName
 * @returns {Representation|undefined}
 */
const getRepresentation = (
  context: ContextFrame | undefined,
  contextFrameName: ContextFrameNamesModal,
): Representation | undefined => {
  if (!context) throw new Error();

  const transformedContext = filterContextsByComponentType(context);

  const componentType = getComponentType(transformedContext);
  const contextFrameConfirmLabelExist = getContextFrameConfirmLabelExist(transformedContext);
  const promptIsRequired = getIsPromptRequired(transformedContext, componentType);
  const footer = getFooter(transformedContext, contextFrameName);

  return {
    type: componentType,
    promptIsRequired,
    contextFrameConfirmLabelExist,
    purposeLimitationsCategories: transformedContext.contexts.map(
      context => context.purposeLimitationCategory,
    ),
    footer,
  };
};

export { getRepresentation };
